import React from "react";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { Box, Typography } from "@mui/material";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import {
    ghostGuideCalloutSx,
    ghostGuideCardSx,
    ghostGuideFreeHintBtnSx,
    ghostGuideHowToTitleSx,
    ghostGuideOverlayRootSx,
    ghostGuidePathDotSx,
    ghostGuidePathLineSx,
    ghostGuideRegionSx,
    ghostGuideSceneFooterSx,
    ghostGuideStepNumSx,
    ghostGuideStepRowSx,
    ghostGuideStepTextSx,
} from "../styles/screenshotStyles";

const JM = "mobileScreenshot.sliding";

const HOW_TO_STEPS = ["ghost-step-1", "ghost-step-2", "ghost-step-3"];

/** Floating cute ghosts on the board path. */
const GHOST_SIZE_PX = 40;
export const GHOST_GUIDE_PNG = `${process.env.PUBLIC_URL || ""}/v4/ghost-guide.png`;

/**
 * Ultra-cute floating ghost PNG (teal glow, doe eyes, blush, star).
 */
function GuideGhost({ cellIndex, gridSize }) {
    const pos = cellTopRightStyle(cellIndex, gridSize);
    return (
        <Box
            component="img"
            src={GHOST_GUIDE_PNG}
            alt=""
            sx={{
                ...ghostGuidePathDotSx,
                ...pos,
                width: GHOST_SIZE_PX,
                height: GHOST_SIZE_PX,
                objectFit: "contain",
                zIndex: 10,
                "@keyframes ghostGuideBobCorner": {
                    "0%, 100%": { transform: "translate(18%, -22%)" },
                    "50%": { transform: "translate(18%, calc(-22% - 3px))" },
                },
                animation: "ghostGuideBobCorner 2.6s ease-in-out infinite",
            }}
        />
    );
}

/** Center of a grid cell (percent of board). */
function cellCenterStyle(cellIndex, gridSize) {
    const gs = Number(gridSize);
    if (!Number.isFinite(gs) || gs <= 0) return { top: "50%", left: "50%" };
    const col = cellIndex % gs;
    const row = Math.floor(cellIndex / gs);
    const cellPct = 100 / gs;
    return {
        top: `${(row + 0.5) * cellPct}%`,
        left: `${(col + 0.5) * cellPct}%`,
        transform: "translate(-50%, -50%)",
    };
}

/** Top-right corner of a grid cell — inset flush so ghost sits in the corner. */
function cellTopRightStyle(cellIndex, gridSize) {
    const gs = Number(gridSize);
    if (!Number.isFinite(gs) || gs <= 0) return { top: 0, right: 0 };
    const col = cellIndex % gs;
    const row = Math.floor(cellIndex / gs);
    const cellPct = 100 / gs;
    return {
        top: `${row * cellPct}%`,
        right: `${(gs - col - 1) * cellPct}%`,
        left: "auto",
    };
}

function StepNumberBadge({ cellIndex, gridSize, label }) {
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                ...cellCenterStyle(cellIndex, gridSize),
                zIndex: 3,
                minWidth: 28,
                height: 28,
                px: 0.5,
                borderRadius: "999px",
                bgcolor: "#0f172a",
                color: "#fff",
                fontSize: "0.85rem",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                pointerEvents: "none",
            }}
        >
            {label}
        </Box>
    );
}

function pathLineBetweenCells(fromIdx, toIdx, gridSize) {
    const gs = Number(gridSize);
    if (!Number.isFinite(gs) || gs <= 0) return null;
    const cellPct = 100 / gs;
    const fromCol = fromIdx % gs;
    const fromRow = Math.floor(fromIdx / gs);
    const toCol = toIdx % gs;
    const toRow = Math.floor(toIdx / gs);
    const x1 = (fromCol + 0.5) * cellPct;
    const y1 = (fromRow + 0.5) * cellPct;
    const x2 = (toCol + 0.5) * cellPct;
    const y2 = (toRow + 0.5) * cellPct;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    return {
        top: `${y1}%`,
        left: `${x1}%`,
        width: `${len}%`,
        transform: `rotate(${angle}deg)`,
        transformOrigin: "0 50%",
        zIndex: 1,
    };
}

/** Just under a grid cell (percent of board), centered on the cell. */
function cellBelowStyle(cellIndex, gridSize) {
    const gs = Number(gridSize);
    if (!Number.isFinite(gs) || gs <= 0) return { top: "50%", left: "50%" };
    const col = cellIndex % gs;
    const row = Math.floor(cellIndex / gs);
    const cellPct = 100 / gs;
    return {
        top: `${(row + 1) * cellPct + 1.2}%`,
        left: `${(col + 0.5) * cellPct}%`,
        transform: "translate(-50%, 0)",
    };
}

/**
 * Board overlay: green path, centered step numbers, and a single ghost
 * in the top-right of the first hint cell.
 */
export function ScreenshotGhostGuideOverlay({ callout, gridSize, moves = [] }) {
    const list = Array.isArray(moves) ? moves : [];
    const cells = list.map((m) => Number(m?.from)).filter((n) => Number.isFinite(n));
    const firstCell = cells.length ? cells[0] : null;

    return (
        <Box sx={ghostGuideOverlayRootSx} aria-hidden>
            {cells.slice(0, -1).map((fromIdx, i) => {
                const line = pathLineBetweenCells(fromIdx, cells[i + 1], gridSize);
                if (!line) return null;
                return (
                    <Box
                        key={`path-${fromIdx}-${cells[i + 1]}`}
                        sx={{ ...ghostGuidePathLineSx, ...line }}
                    />
                );
            })}
            {cells.map((cellIndex, idx) => (
                <StepNumberBadge
                    key={`step-num-${idx}-${cellIndex}`}
                    cellIndex={cellIndex}
                    gridSize={gridSize}
                    label={String(idx + 1)}
                />
            ))}
            {firstCell != null ? (
                <GuideGhost cellIndex={firstCell} gridSize={gridSize} />
            ) : null}
            {callout && firstCell != null ? (
                <Box
                    sx={{
                        ...ghostGuideCalloutSx,
                        ...cellBelowStyle(firstCell, gridSize),
                    }}
                >
                    {callout}
                </Box>
            ) : null}
        </Box>
    );
}

/**
 * How to Use card + free ghost hint CTA.
 * Intentionally omits any Pro / path-tracing upgrade row.
 */
export default function ScreenshotGhostGuidePanel({ slug, label }) {
    const { t } = useScreenshotTranslation();

    return (
        <Box sx={ghostGuideRegionSx}>
            <Box sx={ghostGuideCardSx}>
                <Typography component="h2" sx={ghostGuideHowToTitleSx}>
                    {t(`${JM}.ghost-how-to-title`)}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1, mt: 1.25 }}>
                    {HOW_TO_STEPS.map((key, idx) => (
                        <Box key={key} sx={ghostGuideStepRowSx}>
                            <Box sx={ghostGuideStepNumSx}>{idx + 1}</Box>
                            <Typography component="p" sx={ghostGuideStepTextSx}>
                                {t(`${JM}.${key}`)}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={ghostGuideFreeHintBtnSx}>
                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
                    {t(`${JM}.ghost-free-hint-cta`)}
                </Box>
            </Box>

            {slug && label ? (
                <Typography component="p" sx={ghostGuideSceneFooterSx}>
                    {slug} {label}
                </Typography>
            ) : null}
        </Box>
    );
}
